import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Security,
  Lock,
  ChildCare,
  ContactSupport,
  ArrowBack,
  Storage,
  PhoneIphone
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const BabySoundboardPrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Privacy Policy | Baby Soundboard & Toddler Games',
    description: 'Privacy policy for Baby Soundboard & Toddler Games, a free iPhone kids app that collects no data, has no ads, and keeps settings on the device.',
    path: '/baby-soundboard-privacy-policy'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: October 15, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: Baby Soundboard &amp; Toddler Games
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Also known as: Baby Touchtiles (the name this app was first published under)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: iOS (iPhone and iPad)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              Overview
            </Typography>
            <Typography variant="body1" paragraph>
              Baby Soundboard &amp; Toddler Games is a free play app for babies and toddlers. Taps play bundled sounds, sparkles, and spoken numbers and letters. The App is made for children ages 5 and under, with grown-up settings kept behind a parent gate.
            </Typography>
            <Typography variant="body1" paragraph>
              We do not operate accounts. The App has no ads, no in-app purchases, and no subscriptions. It does not collect personal information, it does not track anyone, and it does not send data to us or to any advertising or analytics company. It works offline.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Lock sx={{ mr: 1 }} />
              Information We Do Not Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We do not collect names, email addresses, photos, videos, voice recordings, precise or coarse location, contacts, identifiers for advertising, or any other personal information. There is no sign-in. We do not receive payment card numbers, Apple ID passwords, or billing details.
            </Typography>
            <Typography variant="body1" paragraph>
              The App does not use advertising networks, analytics SDKs, or tracking technologies that identify a person across other companies&apos; apps or websites. It does not access the microphone, camera, photo library, contacts, or location.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Storage sx={{ mr: 1 }} />
              Information Stored on Your Device
            </Typography>
            <Typography variant="body1" paragraph>
              Grown-up settings are saved only on the device, in local app storage, so the App can remember them the next time it opens. That on-device record may include:
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Which games are turned on, and the order they appear</li>
              <li>Volume limit, spoken-voice language, and whether haptics are on</li>
              <li>Play-timer length, whether the screen stays awake, and whether leaving a game requires the grown-up gate</li>
              <li>Whether the one-time App Store rating prompt has already been shown</li>
              <li>Whether grown-up setup has been completed</li>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              None of this leaves the device. We have no server that receives it. Deleting the App removes this local data, subject to how iOS handles app deletion.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIphone sx={{ mr: 1 }} />
              Sounds, Speech, and Device Features
            </Typography>
            <Typography variant="body1" paragraph>
              Sounds and artwork are bundled inside the App. Spoken numbers and letters use Apple&apos;s on-device speech synthesizer. Nothing is recorded, uploaded, or streamed.
            </Typography>
            <Typography variant="body1" paragraph>
              The App may use system haptics, keep the screen awake during play, and open the iOS Settings app from the grown-up area so a parent can turn on Guided Access. Those are Apple system features on the device. The kid side of the App does not open websites or other apps.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              App Store Rating Prompt
            </Typography>
            <Typography variant="body1" paragraph>
              After a grown-up opens the parent area, the App may ask Apple to show its standard rating prompt once. That prompt is provided by Apple. We do not receive the rating, a review, or an identifier from it. Apple&apos;s own privacy policy describes how Apple handles App Store activity, including downloads of a free app.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ChildCare sx={{ mr: 1 }} />
              Children&apos;s Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              This App is directed at children. We do not knowingly collect personal information from children, and the App does not ask a child for any. There is nothing for a child to type, no account to create, and no way for a child to make a purchase or contact us from inside the App.
            </Typography>
            <Typography variant="body1" paragraph>
              Parent controls, including which games appear, the play timer, the volume cap, and the gate for leaving a game, are on the grown-up side of the App.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Your Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You can change settings in the grown-up area or delete the App to remove the local settings described above. Because we do not hold personal information about you or your child, there is no account data for us to access, correct, or delete. Privacy questions can be sent to{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
              .
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. The effective date at the top of this page will change when an update is posted. Continued use of the App after an update means you accept the revised policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              Michael R. Ellis
              <br />
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default BabySoundboardPrivacyPolicy;
